import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, ArrowUpRight, Truck, Package, XCircle, CheckCircle } from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Same generic message for every failure — no enumeration signal.
const NOT_FOUND_MSG = "We couldn't find an order matching those details.";

export default function OrderTracking() {
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [orderRef, setOrderRef] = useState("");
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // One-click tracking from the confirmation email: `/track?token=…`
  // Runs once on mount if a token is present in the URL.
  useEffect(() => {
    const token = (params.get("token") || "").trim();
    if (!token) return;
    lookup({ token });
    // Strip the token from the visible URL so it isn't shared/screenshot-leaked.
    // The state we've already fetched stays in memory.
    setParams({}, { replace: true });
  }, []);

  const lookup = async (payload) => {
    setLoading(true);
    setError("");
    setOrder(null);
    setNotFound(false);
    try {
      const q = new URLSearchParams(payload);
      const r = await fetch(`${BACKEND}/api/merch/track?${q.toString()}`);
      if (r.status === 429) {
        setError("Too many lookups. Please wait a minute before trying again.");
        return;
      }
      const raw = await r.text();
      let d = null;
      try { d = raw ? JSON.parse(raw) : null; } catch (_e) { d = null; }
      if (!r.ok || !d) {
        setNotFound(true);
        return;
      }
      if (!d.order) {
        setNotFound(true);
        return;
      }
      setOrder(d.order);
    } catch (_e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const search = (e) => {
    e.preventDefault();
    const em = email.trim();
    const on = orderRef.trim();
    if (!em || !on) {
      setError("Enter your checkout email AND your order number.");
      return;
    }
    lookup({ email: em, order: on });
  };

  return (
    <main data-testid="tracking-page" className="min-h-screen bg-[#0A0A0A] text-white font-body pt-16">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">
        <p className="text-[#B32027] uppercase tracking-[0.4em] text-[10px] font-bold mb-4">/ Order tracking</p>
        <h1 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] mb-4">
          Track the{" "}<br /><span className="text-[#B32027]">tribute.</span>
        </h1>
        <p className="text-white/70 text-base leading-relaxed max-w-xl mb-10">
          Use the <strong>Track your order</strong> link from your confirmation email for one-click access &mdash; or enter both your checkout email and your order number below.
        </p>

        <form onSubmit={search} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-6" data-testid="track-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Checkout email"
            data-testid="track-email"
            required
            className="bg-black border-2 border-white/15 px-4 py-3 text-white focus:border-[#B32027] outline-none placeholder:text-white/30"
          />
          <input
            type="text"
            value={orderRef}
            onChange={(e) => setOrderRef(e.target.value)}
            placeholder="Order number"
            data-testid="track-order-ref"
            required
            className="bg-black border-2 border-white/15 px-4 py-3 text-white focus:border-[#B32027] outline-none placeholder:text-white/30"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="track-submit"
            className="inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-3 text-[10px] font-bold uppercase tracking-[0.3em] disabled:opacity-50"
          >
            <MagnifyingGlass size={12} weight="bold" />
            {loading ? "Searching…" : "Track"}
          </button>
        </form>

        {error && (
          <div data-testid="track-error" className="border border-red-500 bg-red-500/10 p-3 text-sm text-red-300 mb-6">
            {error}
          </div>
        )}

        {notFound && (
          <div data-testid="track-not-found" className="border-2 border-white/15 bg-[#0F0F0F] p-8 text-center">
            <p className="text-white/70">{NOT_FOUND_MSG}</p>
            <p className="text-white/40 text-xs mt-2 uppercase tracking-[0.2em]">
              Double-check the details, or contact aj@sweatwithslay.com
            </p>
          </div>
        )}

        {order && <OrderCard order={order} />}
      </div>
    </main>
  );
}

function OrderCard({ order }) {
  const phase = order.phase || "received";
  const badgeStyle =
    phase === "shipped" ? "border-emerald-500 text-emerald-300 bg-emerald-500/10" :
    phase === "delivered" ? "border-emerald-500 text-emerald-300 bg-emerald-500/10" :
    phase === "printing" ? "border-yellow-500 text-yellow-300 bg-yellow-500/10" :
    phase === "preparing" ? "border-[#B32027] text-white bg-[#B32027]/15" :
    phase === "canceled" ? "border-red-500 text-red-300 bg-red-500/10" :
    "border-white/25 text-white/85 bg-white/5";

  return (
    <article data-testid="track-order-card" className="border-2 border-white/15 bg-[#0F0F0F] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          {order.order_number && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
              / Order #{order.order_number}
            </p>
          )}
          {order.items?.map((it, i) => (
            <div key={i} className="mb-2 last:mb-0">
              <h2 className="font-display font-bold text-lg md:text-xl uppercase tracking-tight leading-tight">
                {it.product_name}
              </h2>
              <p className="text-white/60 text-sm mt-0.5">
                {it.variant_name}{it.variant_name && it.quantity ? " · " : ""}Qty {it.quantity}
              </p>
            </div>
          ))}
        </div>
        <span data-testid="track-status-badge" className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-bold border-2 ${badgeStyle}`}>
          {order.status}
        </span>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-sm border-t border-white/10 pt-4">
        <Row label="Total" value={order.total != null ? `$${Number(order.total).toFixed(2)}` : "—"} />
        <Row label="Placed" value={order.placed_on || "—"} />
        {order.ship_to_short && <Row label="Ship to" value={order.ship_to_short} />}
      </div>

      {order.tracking?.tracking_url && (
        <a
          href={order.tracking.tracking_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="track-carrier-link"
          className="mt-5 inline-flex items-center gap-2 border-2 border-[#B32027] bg-[#B32027] hover:bg-[#D42730] text-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.3em]"
        >
          <Truck size={13} weight="bold" />
          Track shipment
          {order.tracking.carrier ? ` · ${order.tracking.carrier}` : ""}
          {order.tracking.tracking_number ? ` · ${order.tracking.tracking_number}` : ""}
          <ArrowUpRight size={12} weight="bold" />
        </a>
      )}

      {phase === "received" && (
        <div className="mt-4 flex items-start gap-2 text-white/60 text-xs">
          <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
          <span>Your payment is confirmed and your order has been received. You&rsquo;ll receive tracking by email once your order enters production and ships.</span>
        </div>
      )}
      {phase === "preparing" && (
        <div className="mt-4 flex items-start gap-2 text-white/60 text-xs">
          <Package size={16} weight="fill" className="text-[#B32027] flex-shrink-0 mt-0.5" />
          <span>Your order is being prepared. Tracking will land in your inbox once it ships.</span>
        </div>
      )}
      {phase === "printing" && (
        <div className="mt-4 flex items-start gap-2 text-white/60 text-xs">
          <Package size={16} weight="fill" className="text-yellow-400 flex-shrink-0 mt-0.5" />
          <span>Your order is being printed right now.</span>
        </div>
      )}
      {phase === "canceled" && (
        <div className="mt-4 flex items-start gap-2 text-red-300 text-xs">
          <XCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>This order was canceled. Contact aj@sweatwithslay.com if you have questions.</span>
        </div>
      )}
    </article>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-1">{label}</p>
      <p className="text-white text-sm">{value || "—"}</p>
    </div>
  );
}
